import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ActionCard from './ActionCard';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import useActionInitialData from '@behavior/activity/getActionInitialData';
import { ActivityInitialDataResponse } from '@components/Activity/types/ActivityInitialDataResponse';

type Props = {
  actionCard: number[];
  setActionCard: (actionCard: number[]) => void;
  count: number;
  setCount: (count: number) => void;
  initialData?: ActivityInitialDataResponse;
  actionCardContent: ActionDTO;
  setActionCardContent: (actionCardContent: ActionDTO) => void;
  actionCardContentList: ActionDTO[];
  setActionCardContentList: (actionCardContentList: ActionDTO[]) => void;
  disabled?: boolean;
};

const INK = '#111418';
const TEXT = '#1f2328';
const TEXT_MUTED = '#8b95a3';
const BORDER = '#e6e8ec';
const SURFACE_SUBTLE = '#f7f8fa';

const ActionRequired = ({
  actionCard,
  setActionCard,
  count,
  setCount,
  initialData,
  actionCardContentList,
  setActionCardContentList,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const getInitialActionData = useActionInitialData();
  const [actionInitial, setActionInitial] = useState<ActionDTO>();

  const addActionCard = () => {
    setCount(count + 1);
    getInitialActionData.getActionInitialData().then(newInitialData => {
      setActionInitial(newInitialData);
    });
    setActionCard([...actionCard, count]);
  };

  useEffect(() => {
    if (actionInitial !== undefined) {
      setActionCardContentList([...actionCardContentList, actionInitial]);
    }
  }, [actionInitial]);

  const removeActionCard = (cardKey: number) => {
    const updatedCards = actionCard.filter(key => key !== cardKey);
    setActionCard(updatedCards);
    const updatedCardsList = actionCardContentList.filter((_, index) => index !== cardKey);
    setActionCardContentList(updatedCardsList);
  };

  const total = actionCardContentList?.length ?? 0;

  return (
    <Stack spacing={1.5}>
      {!disabled && (
        <Box>
          <Button
            onClick={addActionCard}
            disableElevation
            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: INK,
              color: '#ffffff',
              fontSize: 13,
              fontWeight: 500,
              textTransform: 'none',
              height: 32,
              px: 1.75,
              borderRadius: '6px',
              border: `1px solid ${INK}`,
              '&:hover': { bgcolor: '#000000' },
            }}
          >
            {t('activity.details.page.components.activity.action.add')}
          </Button>
        </Box>
      )}

      {total === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${BORDER}`,
            borderRadius: '10px',
            py: 4,
            px: 3,
            textAlign: 'center',
            bgcolor: SURFACE_SUBTLE,
          }}
        >
          <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: TEXT, mb: 0.5 }}>
            {t('action.form.empty.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: TEXT_MUTED }}>
            {t('action.form.empty.subtitle')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {actionCardContentList
            .map((content, originalIndex) => ({ content, originalIndex }))
            .sort((a, b) => {
              // Sort by creation date, newest first. Unsaved cards (no
              // createDte yet) bubble to the top since they're the ones the
              // user is actively working on.
              const aTime = a.content.createDte
                ? new Date(a.content.createDte).getTime()
                : Number.POSITIVE_INFINITY;
              const bTime = b.content.createDte
                ? new Date(b.content.createDte).getTime()
                : Number.POSITIVE_INFINITY;
              return bTime - aTime;
            })
            .map(({ content, originalIndex }) => (
              <ActionCard
                key={originalIndex}
                onRemove={() => removeActionCard(originalIndex)}
                actionContent={content}
                setActionCardContent={(newContent: ActionDTO) => {
                  const updatedList = [...actionCardContentList];
                  updatedList[originalIndex] = newContent;
                  setActionCardContentList(updatedList);
                }}
                initialData={initialData}
                disabled={disabled}
                index={originalIndex}
              />
            ))}
        </Stack>
      )}
    </Stack>
  );
};

export default ActionRequired;
